import { Cell } from "@highmarch/domain";
import type { Enemy, LevelSim, Tower } from "@highmarch/domain";
import type { SceneRig } from "../render/scene";
import type { LevelScene } from "./levelScene";
import { ScreenPoint, nearestDrawn, towerPickPoints } from "./screenPick";

/**
 * What a finger means on the map, judged on screen: the tower, enemy or cell drawn nearest it, within a thumb's reach
 * at the current zoom. Picked by the ground cell under the finger, a tap on a tall tower's roof chose the cell behind
 * it, and a cell only ~40 px across on a phone was hard to hit (screenPick.ts).
 */
export class ScreenPicker {
  /** How far from an enemy's drawn body or badge a long-press still finds it, in CSS pixels: a fingertip. */
  static readonly EnemyPickReachPx = 44;
  /** A tap's reach where cells are drawn big, as a share of a cell's on-screen size: to the cell's corners and a bit. */
  static readonly TapReachCells = 0.8;
  /** How far from a legal cell's centre a tap still chooses it, in CSS pixels: about a fingertip and a half. */
  static readonly TowerTapReachPx = 56;

  constructor(private readonly rig: SceneRig, private readonly sim: LevelSim, private readonly scene: LevelScene) {}

  /** World point → CSS pixels, as the finger sees it. */
  private projector(): (x: number, y: number, z: number) => ScreenPoint {
    const rect = this.rig.canvas.getBoundingClientRect();
    return (x, y, z) => { const p = this.rig.iso.worldToViewport(x, y, z); return { x: rect.left + p.x, y: rect.top + p.y }; };
  }

  /** The live tower drawn nearest the finger — at its foot, the middle of its body or high on it — within a tap's reach. */
  towerAt(x: number, y: number): Tower | null {
    const project = this.projector();
    return nearestDrawn(x, y, this.sim.towerSlots.filter((t) => t.alive), (t) => towerPickPoints(this.rig.cellOnScreen(t.cell), this.scene.towers.restBox(t.id), project), this.tapReachPx(x, y));
  }

  /** The live enemy drawn nearest the finger (its body or its badge plate), within EnemyPickReachPx. */
  enemyAt(x: number, y: number): Enemy | null {
    const project = this.projector();
    return nearestDrawn(x, y, this.sim.enemySlots.filter((e) => e.alive), (e) => (this.scene.enemies.drawnPoints(e.id) ?? []).map((p) => project(p.x, p.y, p.z)), ScreenPicker.EnemyPickReachPx);
  }

  /** An enemy's drawn body on screen (between its middle and its badge), or null before its view exists. */
  enemyOnScreen(id: number): ScreenPoint | null {
    const pts = this.scene.enemies.drawnPoints(id);
    if (!pts) return null;
    const project = this.projector();
    const a = project(pts[0].x, pts[0].y, pts[0].z), b = project(pts[1].x, pts[1].y, pts[1].z);
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  /**
   * The legal tower cell nearest the finger ON SCREEN, within a thumb's reach. Measured in screen pixels against each
   * cell's centre where it is drawn (on the terrain), so it picks the frame the player can see under the thumb, and a
   * cell only ~40 px across on a phone is as easy to hit as a finger-sized target.
   */
  nearestTowerCell(x: number, y: number): Cell | null {
    return this.nearestOnScreen(x, y, this.sim.collectValidTowerCells(), (c) => c);
  }

  /**
   * Of `items`, the one whose cell is drawn nearest the screen point, within reach; null if none is. The reach is a
   * fingertip and a half, or most of a cell where cells are drawn bigger than that (zoomed in): at a fixed 56 px a tap
   * on the corner of a big highlighted cell could miss it.
   */
  nearestOnScreen<T>(x: number, y: number, items: readonly T[], cellOf: (item: T) => Cell): T | null {
    let best: T | null = null, bestD = this.tapReachPx(x, y) ** 2;
    for (const it of items) {
      const p = this.rig.cellOnScreen(cellOf(it));
      const d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d < bestD) { bestD = d; best = it; }
    }
    return best;
  }

  /** How far a tap reaches around it, in CSS pixels, at the zoom and spot on the map where it lands. */
  private tapReachPx(x: number, y: number): number {
    const c = this.rig.pickCell(x, y, this.sim.grid) ?? this.sim.grid.base;
    const a = this.rig.cellOnScreen(c), b = this.rig.cellOnScreen(new Cell(c.x + 1, c.y)), d = this.rig.cellOnScreen(new Cell(c.x, c.y + 1));
    return tapReach(Math.max(Math.hypot(b.x - a.x, b.y - a.y), Math.hypot(d.x - a.x, d.y - a.y)));
  }

  /** The ground point under a client point (canvas px), for the whole map's zoom back in. */
  groundUnder(x: number, y: number): { x: number; z: number } | null {
    const r = this.rig.canvas.getBoundingClientRect();
    return this.rig.iso.viewportToGround(x - r.left, y - r.top);
  }
}

/** A tap's reach (CSS px) where a cell is drawn `cellPx` across: a fingertip and a half, or most of a big cell. */
export function tapReach(cellPx: number): number {
  return Math.max(ScreenPicker.TowerTapReachPx, cellPx * ScreenPicker.TapReachCells);
}
